import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Utensils, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { Modal, Button, Alert } from '../ui';
import { errMsg } from './constants';

/**
 * HỘP THOẠI "Bạn đang chấm công ở nơi khác — ăn cơm thế nào?".
 * Ngôn ngữ đời thường, mỗi lựa chọn là một nút lớn; cử cơm là ô tích. Cử quá giờ chốt vẫn chọn được nếu địa điểm cho sửa
 * muộn — khi đó hiện nhãn "quá giờ chốt" để người dùng hiểu suất sẽ được gắn cờ cho người báo cơm.
 */
export default function TravelPromptModal({ prompt, onClose, onAnswered }) {
  const [choice, setChoice] = useState(null);
  const [slots, setSlots] = useState({});
  const [saving, setSaving] = useState(false);

  const option = useMemo(() => prompt?.options.find((o) => o.value === choice) || null, [prompt, choice]);

  // Mở câu hỏi mới → đặt lại; nếu đã từng trả lời thì chọn sẵn câu trả lời cũ để sửa.
  useEffect(() => {
    if (!prompt) return;
    setChoice(prompt.answer || null);
    const init = {};
    const ans = new Set(prompt.answer_slot_ids || []);
    prompt.options.forEach((o) => o.slots.forEach((s) => { init[`${o.value}|${s.slot_id}`] = prompt.answer === o.value ? ans.has(s.slot_id) : !!s.suggested; }));
    setSlots(init);
  }, [prompt]);

  if (!prompt) return null;
  const picked = option ? option.slots.filter((s) => slots[`${option.value}|${s.slot_id}`]) : [];
  const needSlots = option?.needs_slots;

  const submit = async () => {
    if (!option) return;
    if (needSlots && !picked.length) { toast.error('Chọn ít nhất một bữa'); return; }
    setSaving(true);
    try {
      const r = await api.post(`/meals/travel/prompts/${prompt.prompt_id}/answer`, { choice: option.value, slot_ids: picked.map((s) => s.slot_id) });
      const failed = (r.data.data.result || []).filter((x) => !x.ok);
      if (failed.length) toast.error(failed.map((f) => `${f.slot_code}: ${f.reason}`).join('; '));
      else toast.success('Đã ghi nhận lựa chọn của bạn');
      onAnswered?.(r.data.data.prompt);
      onClose?.();
    } catch (e) { toast.error(errMsg(e, 'Không gửi được câu trả lời')); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Bạn đang chấm công ở nơi khác" size="lg"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Để sau</Button>
          <Button onClick={submit} loading={saving} disabled={!option} leftIcon={Check}>{prompt.answer ? 'Cập nhật lựa chọn' : 'Gửi lựa chọn'}</Button>
        </>
      )}>
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl bg-brand-softer border border-brand p-3 text-brand-fg-strong">
          <MapPin className="w-6 h-6 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <div className="text-lg font-bold leading-snug">{prompt.question}</div>
            <div className="text-sm mt-1">Ngày {prompt.meal_date.slice(8, 10)}/{prompt.meal_date.slice(5, 7)}. Nơi làm việc chính của bạn: <b>{prompt.home_location_name}</b>.</div>
          </div>
        </div>

        <div role="radiogroup" aria-label="Chọn cách ăn" className="space-y-2">
          {prompt.options.map((o) => {
            const on = choice === o.value;
            return (
              <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => setChoice(o.value)}
                className={`w-full text-left rounded-xl border-2 px-4 py-3 min-h-[56px] transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-medium ${on ? 'border-brand bg-brand-softer' : 'border-line-strong bg-surface hover:bg-surface-sunken'}`}>
                <div className="flex items-center gap-2 text-base font-bold text-heading"><Utensils className="w-5 h-5 flex-shrink-0" aria-hidden="true" />{o.label}</div>
                {o.hint ? <div className="text-sm text-body mt-0.5">{o.hint}</div> : null}
                {on && o.slots.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2" onClick={(ev) => ev.stopPropagation()}>
                    {o.slots.map((s) => {
                      const k = `${o.value}|${s.slot_id}`;
                      const checked = !!slots[k];
                      return (
                        <label key={s.slot_id} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer min-h-[44px] ${s.can_apply ? '' : 'opacity-50 cursor-not-allowed'} ${checked ? 'border-brand bg-surface' : 'border-line-strong bg-surface-sunken'}`}>
                          <input type="checkbox" className="w-5 h-5" disabled={!s.can_apply} checked={checked} onChange={(ev) => setSlots((p) => ({ ...p, [k]: ev.target.checked }))} />
                          <span className="text-base font-medium text-heading">{s.slot_name}</span>
                          <span className="text-xs text-muted">chốt {s.cutoff_label}{s.late ? ' · QUÁ GIỜ CHỐT' : ''}{!s.can_apply ? ' · không sửa muộn được' : ''}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>

        {option && picked.some((s) => s.late) ? (
          <Alert variant="warning">Có bữa đã quá giờ chốt. Hệ thống vẫn ghi nhận nhưng gắn nhãn “sau giờ chốt” để người báo cơm và người theo dõi biết.</Alert>
        ) : null}
        {prompt.status === 'answered' ? <Alert variant="brand">Bạn đã trả lời: <b>{prompt.answer_label}</b>. Có thể đổi lại ở đây — mọi lần đổi đều được ghi lại.</Alert> : null}
      </div>
    </Modal>
  );
}
